import { useEffect, useMemo, useState } from "react";
import api from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { useUserProfile } from "../contexts/UserProfileContext";
import { weekStart } from "../utils/activitySummary";
import { weeklyLoads, fitnessSeries } from "../utils/trainingLoad";

const LOAD_HISTORY_WEEKS = 12;
const LOAD_HISTORY_LIMIT = 200; // the API's cap

const readLocal = () => {
  try {
    return JSON.parse(localStorage.getItem("completedSessions") || "[]");
  } catch {
    return [];
  }
};

/**
 * Weekly training load (#94) and fitness (#203), for Activity's Progress card
 * and the Progress page. They want 12 weeks: 8 to chart, plus 4 for the
 * 42-day fitness average to settle. The Profile's recent list stops at 50
 * sessions, so this fetches its own window, again whenever that list changes
 * (a session saved or edited). `refresh` is anything else that should re-read
 * it, such as a page's own list of sessions.
 */
export default function useTrainingLoad(refresh) {
  const { isAuthenticated } = useAuth();
  const { sessions: recentSessions } = useUserProfile();
  const [history, setHistory] = useState(null);
  useEffect(() => {
    if (!isAuthenticated) {
      setHistory(null);
      return undefined;
    }
    let cancelled = false;
    const from = weekStart(Date.now());
    from.setDate(from.getDate() - 7 * LOAD_HISTORY_WEEKS);
    api
      .getSessions({ from: from.toISOString(), limit: LOAD_HISTORY_LIMIT })
      .then((list) => !cancelled && setHistory(Array.isArray(list) ? list : null))
      .catch(() => !cancelled && setHistory(null));
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, recentSessions]);
  const source = useMemo(() => {
    if (!isAuthenticated) return { list: readLocal(), truncated: false };
    if (history) return { list: history, truncated: history.length >= LOAD_HISTORY_LIMIT };
    const list = recentSessions || [];
    return { list, truncated: list.length >= 50 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, history, recentSessions, refresh]);
  const loads = useMemo(() => weeklyLoads(source.list, { truncated: source.truncated }), [source]);
  // A list cut off at the limit may be missing its oldest days, which would
  // start fitness low; better none than a wrong one.
  const fitness = useMemo(() => (source.truncated ? null : fitnessSeries(source.list)), [source]);
  return { loads, fitness };
}
